import { FC, useCallback } from 'react';
import { useToast } from '@ordr-as/umami-react/toast';
import useIntl from '../../../../../../hooks/useIntl';
import FormattedMessage from '../../../../../../components/FormattedMessage';
import useMutateCancelPayment from '../../../../../../hooks/usePaymentCancelledApi';
import { ListActivePaymentDto, PaymentState } from '../../../../../../../generated-api';
import CartItems from './CartItems';
import { useLocation } from '../../../../../../context/LocationContext';
import { useCartStore } from '../../../../../../context/cart/CartStore';
import { shallow } from 'zustand/shallow';
import { getPaymentMethodById } from '../../../../../../context/PaymentMethodStore';
import { Dialog } from '@ordr-as/umami-react/dialog';

interface Props {
  onCloseDialog: () => void;
  focusedPayment: ListActivePaymentDto;
  isOpen: boolean;
  removeActivePayment: (activePayment: ListActivePaymentDto) => void;
}

const FocusedPaymentDialog: FC<Props> = ({ isOpen, onCloseDialog, focusedPayment, removeActivePayment }) => {
  const intl = useIntl();
  const { location } = useLocation();
  const { mutate } = useMutateCancelPayment();
  const { cartIsEmpty, replaceCart } = useCartStore(
    (state) => ({
      cartIsEmpty: state.cart.orderLines.length < 1,
      replaceCart: state.overrideCart,
    }),
    shallow
  );

  const toasts = useToast();

  const isCancelledPayment = focusedPayment?.paymentState === PaymentState.FAILED;

  const cancelPayment = useCallback(() => {
    if (focusedPayment.paymentReference) {
      mutate({
        paymentReference: focusedPayment.paymentReference,
        locationId: location.locationId,
        cancelPaymentDto: {
          terminalId: focusedPayment.paymentMethod.id,
          isUsingHub: getPaymentMethodById(focusedPayment.paymentMethod.id)?.isUsingHub || false,
        },
      });
    }
  }, [focusedPayment.paymentMethod.id, focusedPayment.paymentReference, location.locationId, mutate]);

  const discardOrder = useCallback(() => {
    if (!focusedPayment) {
      return;
    }

    if (focusedPayment?.paymentReference) {
      toasts.remove(focusedPayment.paymentReference);
    }
    cancelPayment();
    removeActivePayment(focusedPayment);
    onCloseDialog();
  }, [cancelPayment, focusedPayment, onCloseDialog, removeActivePayment, toasts]);

  const setItemsBackInCart = useCallback(() => {
    if (!focusedPayment?.cart) {
      return;
    }

    replaceCart(focusedPayment.cart);
    discardOrder();
  }, [discardOrder, focusedPayment?.cart, replaceCart]);

  if (!isOpen || !focusedPayment) {
    return null;
  }

  return (
    <Dialog
      className="w-full mx-4 tablet:w-1/2 tablet:mx-0"
      onClose={onCloseDialog}
      title={`${focusedPayment.paymentMethod.label} - ${intl.formatMessage({
        id: isCancelledPayment ? 'payment.focusDialog.cancelledTitle' : 'payment.focusDialog.inUseTitle',
      })}`}
      actionLabel={intl.formatMessage({
        id: 'payment.focusDialog.putBackAction',
      })}
      onAction={setItemsBackInCart}
      isDisabledAction={!cartIsEmpty}
      secondaryActionLabel={intl.formatMessage({ id: 'payment.focusDialog.discardAction' })}
      onSecondaryAction={discardOrder}
    >
      <p className="mb-4">
        {isCancelledPayment ? (
          <FormattedMessage id="payment.focusDialog.cancelledContent" />
        ) : (
          <FormattedMessage id="payment.focusDialog.inUseContent" />
        )}
      </p>
      <CartItems orderLines={focusedPayment.cart?.orderLines || []} />
    </Dialog>
  );
};

export default FocusedPaymentDialog;
